import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Box } from "@mui/material";
import {
  Dropdown,
  TextArea,
  TextInput,
  DropdownType,
  DropdownOption,
  CircularProgress,
  Tooltip,
} from "cprime-foundational-components";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useDispatch } from "react-redux";
import * as style from "./ApplicationList.module.scss";
import Typography from "../../Components/Common/CprimeText";
import {
  CreateApplicatiomState,
  createApplication,
} from "../../store/slices/CreateApplicationSlice";
import { AppDispatch } from "../../store/store";
import { useNavigate } from "react-router-dom";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import {
  getProtectedByData,
  getValueStreamDropdownData,
  getEmailSearchResults,
  getAzureApplicationSearchResults,
} from "../../services/RequestAccess/RequestAccess";
import { triggerToast } from "../../utils/toast";
import CustomBreadcrumb from "../../Components/CustomBreadcrumb/CustomBreadcrumb";
import { getParamValue } from "../../utils/commonUtil";
import { debounce } from "lodash";

import Button from "../../Components/Common/CprimeButton";
const CreateApplication = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [protectedByOptions, setProtectedByOptions] = useState<
    DropdownOption[]
  >([]);
  const [valueStreamOptions, setValueStreamOptions] = useState<
    DropdownOption[]
  >([]);
  const [primaryContactOptions, setPrimaryContactOptions] = useState<
    DropdownOption[]
  >([]);
  const [businessOwnerOptions, setBusinessOwnerOptions] = useState<
    DropdownOption[]
  >([]);
  const [searchText, setSearchText] = useState("");
  const [searchBO, setSearchBO] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isProtectedByLoading, setIsProtectedByLoading] =
    useState<boolean>(true);
  const [isValueStreamLoading, setIsValueStreamLoading] =
    useState<boolean>(true);
  const [isPrimaryContactLoading, setIsPrimaryContactLoading] =
    useState<boolean>(false);
  const [isBusinessOwnerLoading, setIsBusinessOwnerLoading] =
    useState<boolean>(false);
  const [azureAdOptions, setAzureAdOptions] = useState<DropdownOption[]>([]);
  const [azureAdSearchText, setAzureAdSearchText] = useState("");
  const [isAzureAdLoading, setIsAzureAdLoading] = useState<boolean>(false);
  const [isAzureAdCustom, setIsAzureAdCustom] = useState<boolean>(false);
  const azureAdSelectedRef = useRef<boolean>(false);

  const [formData, setFormData] = useState<CreateApplicatiomState>({
    appCode: "",
    description: "",
    primaryContact: "",
    businessOwner: "",
    applicationName: "",
    appGuid: "",
    protectedBy: "",
    value_stream_id: "",
    primaryContactName: "",
    businessOwnerName: "",
    azure_ad_name: "",
  });

  const isFormValid = Object.values(formData)?.every(
    (value) => value?.trim() !== "",
  );

  const fetchProtectedByData = async () => {
    setIsProtectedByLoading(true);
    try {
      const protectedByResp = await getProtectedByData();
      const mappedOptions = protectedByResp?.data?.map((item: any) => ({
        label: item?.name,
        value: item?.id,
      }));
      setProtectedByOptions(mappedOptions);
    } catch (err) {
      // console.error("Failed to fetch protectedBy data:", err);
    } finally {
      setIsProtectedByLoading(false);
    }
  };

  const fetchValueStreamDropdownData = async () => {
    setIsValueStreamLoading(true);
    try {
      const valueStreamDropdownData = await getValueStreamDropdownData();
      const mappedOptions = valueStreamDropdownData?.data?.map((item: any) => ({
        label: item?.value_stream_name,
        value: item?.value_stream_id,
      }));
      setValueStreamOptions(mappedOptions);
    } catch (err) {
      // console.error(err);
    } finally {
      setIsValueStreamLoading(false);
    }
  };

  const fetchPrimaryContactOptions = async (query: string) => {
    try {
      setIsPrimaryContactLoading(true);
      const { value, param } = getParamValue(query);
      const response = await getEmailSearchResults(
        encodeURIComponent(value),
        param,
      );
      const mapped: DropdownOption[] = (response?.data || []).map(
        (email: any) => ({
          id: email?.mail,
          label:
            param === "name"
              ? email?.mail?.toLowerCase()?.replace(/\./g, " ")
              : email?.mail,
          displayName: email?.displayName,
        }),
      );
      setPrimaryContactOptions(mapped);
    } catch (error) {
      setPrimaryContactOptions([]);
    } finally {
      setIsPrimaryContactLoading(false);
    }
  };

  const fetchBusinessOwnerOptions = async (query: string) => {
    setIsBusinessOwnerLoading(true);
    try {
      const { value, param } = getParamValue(query);
      const response = await getEmailSearchResults(
        encodeURIComponent(value),
        param,
      );
      const mapped: DropdownOption[] = (response?.data || []).map(
        (email: any) => ({
          id: email?.mail,
          displayName: email?.displayName,
          label:
            param === "name"
              ? email?.mail?.toLowerCase()?.replace(/\./g, " ")
              : email?.mail,
        }),
      );
      setBusinessOwnerOptions(mapped);
    } catch (error) {
      setBusinessOwnerOptions([]);
    } finally {
      setIsBusinessOwnerLoading(false);
    }
  };

  const debouncedFetchPrimaryContactOptions = useCallback(
    debounce((val: string) => {
      fetchPrimaryContactOptions(val);
    }, 300),
    [],
  );
  const debouncedFetchBusinessOwnerOptions = useCallback(
    debounce((val: string) => {
      fetchBusinessOwnerOptions(val);
    }, 300),
    [],
  );

  // Determine if Protected By is AzureAD
  const isAzureAD =
    protectedByOptions?.find(
      (opt) => String(opt?.value) === formData?.protectedBy,
    )?.label === "AzureAD";

  const fetchAzureAdOptions = async (query: string) => {
    setIsAzureAdLoading(true);
    try {
      const response = await getAzureApplicationSearchResults(query);
      const mapped: DropdownOption[] = (response?.data || []).map(
        (item: any) => ({
          id: item?.clientId,
          label: item?.displayName,
          objectId: item?.objectId,
        }),
      );
      setAzureAdOptions(mapped);
    } catch {
      setAzureAdOptions([]);
    } finally {
      setIsAzureAdLoading(false);
    }
  };

  const debouncedFetchAzureAdOptions = useCallback(
    debounce((val: string) => {
      fetchAzureAdOptions(val);
    }, 300),
    [],
  );

  // Inject typed text as a selectable "new" option when it doesn't match any existing option
  const azureAdOptionsWithNew = useMemo(() => {
    const trimmed = azureAdSearchText?.trim();
    if (
      trimmed &&
      !azureAdOptions.some(
        (opt) => opt.label?.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      return [
        ...azureAdOptions,
        { id: trimmed, label: trimmed, isNew: true } as any,
      ];
    }
    return azureAdOptions;
  }, [azureAdSearchText, azureAdOptions]);

  // Commit a free-typed Azure AD name (not found in dropdown options)
  const commitAzureAdFreeText = useCallback(() => {
    if (azureAdSelectedRef.current) {
      azureAdSelectedRef.current = false;
      return;
    }
    const trimmed = azureAdSearchText?.trim();
    if (
      trimmed &&
      !azureAdOptions.some(
        (opt) => opt.label?.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      setFormData((prev) => ({
        ...prev,
        azure_ad_name: trimmed,
        appGuid: "", // cleared so user can enter manually
      }));
      setAzureAdSearchText(trimmed);
      setIsAzureAdCustom(true);
    }
  }, [azureAdSearchText, azureAdOptions]);

  useEffect(() => {
    fetchProtectedByData();
    fetchValueStreamDropdownData();
  }, []);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    const newValue =
      name === "appGuid" || name === "appCode"
        ? value?.replace(/\s/g, "")
        : value;

    setFormData({ ...formData, [name]: newValue });
  };

  const handleSubmit = async () => {
    if (isFormValid) {
      setIsLoading(true);
      try {
        const result = await dispatch(createApplication(formData));
        if (result?.payload) {
          triggerToast(
            result?.payload?.message,
            result?.payload?.status === "SUCCESS" ? "success" : "error",
          );

          if (result?.payload?.status === "SUCCESS") {
            navigate("/application-list");
          }
        }
      } catch (error) {
        triggerToast("An unexpected error occurred", "error");
      } finally {
        setIsLoading(false);
      }
    } else {
      triggerToast("Enter data in all fields", "error");
    }
  };

  const scssClass = style as any;

  return (
    <>
      <div className={scssClass.page}>
        <div
          className={scssClass.backButton}
          style={{ width: "100%", marginBottom: "1rem" }}
        >
          <CustomBreadcrumb
            items={[
              {
                label: "Application List",
                href: "/application-list",
                icon: <HomeOutlinedIcon sx={{ color: "#2469FF" }} />,
              },
              { label: "Request Application", href: undefined },
            ]}
          />
        </div>

        <div
          className={scssClass.appDetailContainer}
          style={{
            height: "calc(100vh - 16rem)",
            overflow: "auto",
            marginBottom: "5rem",
          }}
        >
          <div
            className={scssClass.header}
            style={{
              padding: "1.5rem",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <span className={scssClass.title}>New Application Onboarding</span>
            <span className={scssClass.titleSubHeader}>
              Fill out form below and submit to request this application to be
              onboarded
            </span>
          </div>
          <Box
            className={scssClass.applicationDetails}
            style={{
              marginTop: "1rem",
              // marginBottom: "5rem",
              padding: "1.2rem 1.5rem 1.5rem 1.5rem",
              gap: "18px",
            }}
          >
            <div className={scssClass.row} style={{ gap: "1.5rem" }}>
              <div
                className={scssClass.column}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: 0,
                  gap: "8px",
                }}
              >
                <Typography className={scssClass.label}>Name</Typography>
                <TextInput
                  fullWidth
                  data-testid="applicationName-input"
                  value={formData?.applicationName}
                  onChange={handleChange}
                  placeholder="Enter Name"
                  name="applicationName"
                />
              </div>
              <div
                className={scssClass.column}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: 0,
                  gap: "8px",
                }}
              >
                <Typography className={scssClass.label}>
                  Primary Contact Name
                </Typography>
                <Dropdown
                  options={primaryContactOptions}
                  loading={isPrimaryContactLoading}
                  loadingText="Loading..."
                  aria-label="primaryContact"
                  placeholder="Search And Select Contact"
                  optionsTextLimit={200}
                  width={"100%"}
                  inputValue={searchText}
                  selectedOptions={
                    formData?.primaryContact
                      ? [
                          {
                            id: formData?.primaryContact,
                            label: formData?.primaryContact,
                          },
                        ]
                      : []
                  }
                  onOpen={() => fetchPrimaryContactOptions("")}
                  onInputChange={(
                    event: React.SyntheticEvent<Element, Event>,
                    value: string,
                    reason: string,
                  ) => {
                    if (
                      formData?.primaryContact &&
                      value !== formData?.primaryContact
                    ) {
                      setFormData((prev) => ({
                        ...prev,
                        primaryContact: "",
                        primaryContactName: "",
                      }));
                    }

                    setSearchText(value);
                    setIsPrimaryContactLoading(true);
                    debouncedFetchPrimaryContactOptions(value?.trim());
                  }}
                  onChange={(selected: any[]) => {
                    const selectedEmail = String(selected?.[0]?.id || "");
                    setFormData((prev) => ({
                      ...prev,
                      primaryContact: selectedEmail,
                      primaryContactName: selected?.[0]?.displayName || "",
                    }));
                    setSearchText("");
                  }}
                  type={DropdownType.FilterSingleSelect}
                  style={{ width: "100%" }}
                />
              </div>
              <div
                className={scssClass.column}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: 0,
                  gap: "8px",
                }}
              >
                <Typography className={scssClass.label}>
                  Primary Contact Name
                </Typography>
                <TextInput
                  fullWidth
                  data-testid="primaryContactName-input"
                  value={formData?.primaryContactName}
                  placeholder="Enter Primary Contact Name"
                  name="primaryContactName"
                  disabled
                />
              </div>
            </div>
            <div className={scssClass.row} style={{ gap: "1.5rem" }}>
              <div
                className={scssClass.column}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: 0,
                  gap: "8px",
                }}
              >
                <Typography className={scssClass.label}>
                  Business Owner Name
                </Typography>
                <Dropdown
                  options={businessOwnerOptions}
                  loading={isBusinessOwnerLoading}
                  loadingText="Loading..."
                  aria-label="businessOwner"
                  placeholder="Search And Select Contact"
                  optionsTextLimit={200}
                  width={"100%"}
                  inputValue={searchBO}
                  selectedOptions={
                    formData?.businessOwner
                      ? [
                          {
                            id: formData?.businessOwner,
                            label: formData?.businessOwner,
                          },
                        ]
                      : []
                  }
                  onOpen={() => {
                    fetchBusinessOwnerOptions("");
                  }}
                  onInputChange={(
                    event: React.SyntheticEvent<Element, Event>,
                    value: string,
                  ) => {
                    if (
                      formData?.businessOwner &&
                      value !== formData?.businessOwner
                    ) {
                      setFormData((prev) => ({
                        ...prev,
                        businessOwner: "",
                        businessOwnerName: "",
                      }));
                    }

                    setSearchBO(value);
                    setIsBusinessOwnerLoading(true);
                    debouncedFetchBusinessOwnerOptions(value?.trim());
                  }}
                  onChange={(selected: any[]) => {
                    const selectedEmail = String(selected?.[0]?.id || "");
                    setFormData((prev) => ({
                      ...prev,
                      businessOwner: selectedEmail,
                      businessOwnerName: selected?.[0]?.displayName || "",
                    }));
                    setSearchBO("");
                  }}
                  type={DropdownType.FilterSingleSelect}
                  style={{ width: "100%" }}
                />
              </div>
              <div
                className={scssClass.column}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: 0,
                  gap: "8px",
                }}
              >
                <Typography className={scssClass.label}>
                  Business Owner Name
                </Typography>
                <TextInput
                  fullWidth
                  data-testid="businessOwnerName-input"
                  value={formData?.businessOwnerName}
                  placeholder="Enter Business Owner Name"
                  name="businessOwnerName"
                  disabled
                />
              </div>
              <div
                className={scssClass.column}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: 0,
                  gap: "8px",
                }}
              >
                <Typography className={scssClass.label}>
                  Value Stream
                </Typography>
                <Dropdown
                  options={valueStreamOptions}
                  loading={isValueStreamLoading}
                  loadingText="Loading..."
                  aria-label="valueStream"
                  placeholder="Select Value Stream"
                  optionsTextLimit={200}
                  width={"100%"}
                  onChange={async (selected: DropdownOption[]) => {
                    const selectedValueStreamId = String(selected[0]?.value);
                    setFormData((prev) => ({
                      ...prev,
                      value_stream_id: selectedValueStreamId,
                    }));
                  }}
                  selectedOptions={
                    valueStreamOptions?.find(
                      (opt) => String(opt?.value) === formData?.value_stream_id,
                    )
                      ? [
                          valueStreamOptions?.find(
                            (opt) =>
                              String(opt?.value) === formData?.value_stream_id,
                          )!,
                        ]
                      : []
                  }
                  style={{ width: "100%" }}
                />
              </div>
            </div>
            <div className={scssClass.row} style={{ gap: "1.5rem" }}>
              <div
                className={scssClass.column}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: 0,
                  gap: "8px",
                }}
              >
                <Typography className={scssClass.label}>
                  Protected By
                </Typography>
                <Dropdown
                  options={protectedByOptions}
                  loading={isProtectedByLoading}
                  loadingText="Loading..."
                  aria-label="protectedBy"
                  placeholder="Select Protected By"
                  optionsTextLimit={200}
                  width={"100%"}
                  selectedOptions={
                    protectedByOptions?.find(
                      (opt) => String(opt?.value) === formData?.protectedBy,
                    )
                      ? [
                          protectedByOptions?.find(
                            (opt) =>
                              String(opt?.value) === formData?.protectedBy,
                          )!,
                        ]
                      : []
                  }
                  onChange={(selected: DropdownOption[]) => {
                    const selectedProtectedBy = String(selected[0]?.value);
                    setFormData({
                      ...formData,
                      protectedBy: selectedProtectedBy,
                      azure_ad_name: "",
                      appGuid: "",
                    });
                    setAzureAdSearchText("");
                    setAzureAdOptions([]);
                    setIsAzureAdCustom(false);
                  }}
                  style={{ width: "100%" }}
                />
              </div>
              <div
                className={scssClass.column}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: 0,
                  gap: "8px",
                }}
              >
                <Typography className={scssClass.label}>
                  App Object Name
                </Typography>
                {isAzureAD ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <Dropdown
                      options={azureAdOptionsWithNew}
                      loading={isAzureAdLoading}
                      loadingText="Loading..."
                      aria-label="azureAdName"
                      placeholder="Search Or Enter App Object Name"
                      optionsTextLimit={200}
                      width={"100%"}
                      inputValue={azureAdSearchText}
                      selectedOptions={
                        formData?.azure_ad_name
                          ? [
                              {
                                id:
                                  formData?.appGuid || formData?.azure_ad_name,
                                label: formData?.azure_ad_name,
                              },
                            ]
                          : []
                      }
                      onOpen={() => fetchAzureAdOptions("")}
                      onInputChange={(
                        _event: React.SyntheticEvent<Element, Event>,
                        value: string,
                      ) => {
                        if (
                          formData?.azure_ad_name &&
                          value !== formData?.azure_ad_name
                        ) {
                          setFormData((prev) => ({
                            ...prev,
                            azure_ad_name: "",
                            appGuid: "",
                          }));
                          setIsAzureAdCustom(false);
                        }
                        setAzureAdSearchText(value);
                        setIsAzureAdLoading(true);
                        debouncedFetchAzureAdOptions(value?.trim());
                      }}
                      onClose={commitAzureAdFreeText}
                      onChange={(selected: any[]) => {
                        azureAdSelectedRef.current = true;
                        const isNewOption = !!selected?.[0]?.isNew;
                        const selectedClientId = isNewOption
                          ? ""
                          : String(selected?.[0]?.id || "");
                        const selectedDisplayName = String(
                          selected?.[0]?.label || "",
                        );
                        setFormData((prev) => ({
                          ...prev,
                          azure_ad_name: selectedDisplayName,
                          appGuid: selectedClientId,
                        }));
                        setAzureAdSearchText("");
                        setIsAzureAdCustom(isNewOption);
                      }}
                      renderOption={(props: any, option: any) => (
                        <li {...props}>
                          {option?.isNew ? (
                            <Tooltip
                              title="User-provided value (not from AzureAD)"
                              placement="right"
                              arrow
                            >
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: "100%",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span>{option.label}</span>
                                <AddCircleOutlineIcon
                                  style={{
                                    fontSize: "16px",
                                    color: "#000000",
                                    marginLeft: "8px",
                                    flexShrink: 0,
                                  }}
                                />
                              </span>
                            </Tooltip>
                          ) : (
                            <span>{option.label}</span>
                          )}
                        </li>
                      )}
                      type={DropdownType.FilterSingleSelect}
                      style={{ width: "100%" }}
                    />
                  </div>
                ) : (
                  <TextInput
                    fullWidth
                    data-testid="azureAdName-input"
                    value={formData?.azure_ad_name}
                    onChange={handleChange}
                    placeholder="Enter App Object Name"
                    name="azure_ad_name"
                  />
                )}
              </div>
              <div
                className={scssClass.column}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: 0,
                  gap: "8px",
                }}
              >
                <Typography className={scssClass.label}>App GUID</Typography>
                <TextInput
                  fullWidth
                  value={formData?.appGuid}
                  onChange={handleChange}
                  placeholder="Enter App GUID"
                  name="appGuid"
                  disabled={isAzureAD && !isAzureAdCustom}
                />
              </div>
            </div>
            <div className={scssClass.row} style={{ gap: "1.5rem" }}>
              <div
                className={scssClass.column}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: 0,
                  gap: "8px",
                  maxWidth: "calc(33.33% - 1rem)",
                }}
              >
                <Typography className={scssClass.label}>App Code</Typography>
                <TextInput
                  value={formData?.appCode}
                  fullWidth
                  onChange={handleChange}
                  placeholder="Enter 3 Character App Code"
                  name="appCode"
                />
              </div>
            </div>
            <div className={scssClass.row}>
              <div
                className={scssClass.column}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: 0,
                  gap: "8px",
                }}
              >
                <Typography className={scssClass.label}>Description</Typography>
                <TextArea
                  value={formData?.description}
                  onChange={handleChange}
                  name="description"
                  aria-label="minimum height"
                  minRows={3}
                  placeholder="Enter Description"
                  style={{ width: "100%" }}
                  maxLength={300}
                />
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Typography className={scssClass.label}>
                    {formData?.description?.length}/300
                  </Typography>
                </div>
              </div>
            </div>
          </Box>
        </div>
      </div>
      <div
        style={{
          backgroundColor: "white",
          position: "fixed",
          width: "100%",
          padding: "10px 0",
          bottom: 0,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Button
          variant="tertiary"
          sx={{ margin: "0 10px" }}
          size="medium"
          onClick={() => navigate("/application-list")}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          sx={{ margin: "0 10px" }}
          size="medium"
          onClick={handleSubmit}
          disabled={!isFormValid || isLoading}
        >
          {isLoading ? (
            <CircularProgress size="small" data-testid="loading-spinner" />
          ) : (
            "Submit"
          )}
        </Button>
      </div>
    </>
  );
};

export default CreateApplication;
